/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Checkbox, Grid, TextField, Typography } from '@mui/material'
import { FC, useContext, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useSearchParams } from 'react-router-dom'
import { computer } from 'src/assets'
import { getOrderStatusColor } from 'src/components/pages/Private/Calendar'
import { PrivateLoader } from 'src/components/templates'
import Icon from 'src/components/UI/CustomUI/atoms/Icon'
import Tag from 'src/components/UI/CustomUI/atoms/Tag'
import Table from 'src/components/UI/CustomUI/organisms/Table'
import { AlignType } from 'src/components/UI/CustomUI/organisms/Table/types'
import { itemsActions, territoryActions } from 'src/ducks/actions'
import { employeeIsReadOnly, getItemsSortedByCategory, getJob, getOrderById, getOrders, getSiteVisits } from 'src/ducks/selectors'
import { GroupedItem } from 'src/ducks/types'
import { dateFormatString, formatTimestamp, formatTimestampDate, getColorBySeverity, ITEM_STATUS, ORDER_STATUS, round } from 'src/helpers'
import { WorkOrdersContext } from '../context'
import DropDownSelect from './DropDownSelect'
import Header from './Header'
import ItemActions from './ItemActions'
import Status from './Status'
import styles from './styles.module.scss'

const OrderItems: FC<{ orderId?: string | null, fullHeader?: boolean, tabName?: string }> = ({ orderId = null, fullHeader = false, tabName }) => {
  const {
    dispatch: dispatchContext,
    state: { checkAll, expandAll, itemsLoading, refreshSite, selectedOrderId }
  } = useContext(WorkOrdersContext)
  const dispatch = useDispatch()
  const { id } = useParams()
  const itemsByCategory = useSelector(getItemsSortedByCategory())
  const siteVisits = useSelector(getSiteVisits())
  const [tableLoading, setTableLoading] = useState(true)
  const job = useSelector(getJob())
  const [searchParams] = useSearchParams()
  const isReadOnly = useSelector(employeeIsReadOnly)
  const orderSelected = useSelector(getOrderById(selectedOrderId as string))
  const {
    completedTimestamp, vendorPaymentTimestamp,
    statusType, csmAcceptanceTimestamp,

  } = orderSelected

  const orders = useSelector(getOrders())
  const order = searchParams?.get('order') ?? ''

  const showPaid = statusType && (ORDER_STATUS.PAID === statusType || vendorPaymentTimestamp != null)
  const showAccepted = statusType && (ORDER_STATUS.COMPLETE === statusType || showPaid || csmAcceptanceTimestamp != null)
  const showComplete = statusType && (ORDER_STATUS.REVIEW === statusType || showAccepted || completedTimestamp != null)

  const headers = [
    {
      id: 'checkbox',
      custom: true,
      Element: (row: any, index: number): JSX.Element => {
        return (
          <Checkbox
            key={index}
            checked={row.checked || false}
            onClick={(event) => {
              event.stopPropagation()
              const copyWorkItems: GroupedItem[] = [...itemsByCategory]
              const categoryIndex = copyWorkItems.findIndex((category) => category.category === row.category)
              copyWorkItems[categoryIndex].items[index].checked = !copyWorkItems[categoryIndex].items[index].checked
              dispatch(itemsActions.setItemsByCategory(copyWorkItems))
            }}
            color='infoText'
          />
        )
      },
      width: 20
    },
    {
      id: 'inspectionReportReference',
      label: 'Ref #'
    },
    {
      id: 'item',
      label: 'Item',
      custom: true,
      width: 900,
      Element: (row: any): JSX.Element => {
        const { background } = getColorBySeverity(row.severity)
        const followupNotesOrderedByDate = row.followupNotes?.sort((a: any, b: any) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime())
        const isRejectedItem = row.status === ITEM_STATUS.QC_REJECTED;
        const siteVisitStatus = row.siteVisitId ? siteVisits.find((siteVisit: any) => siteVisit.orderId === row.siteVisitId)?.statusType : null
        return (
          <Box marginRight={3}>
            <Box display='flex' flexDirection='column' gap={0.5}>
              <Box display='flex' gap={1}>
                <Typography variant='h6' color='var(--blue700)' sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {row.title}
                </Typography>
                <Tag
                  sx={{ paddingX: 2, textTransform: 'uppercase', height: '25px' }}
                  variant='caption'
                  width='fit-content'
                  background={background}
                  color='var(--black-color)'
                >
                  {row.severity}
                </Tag>
                {row.siteVisitId && (
                  <Tag
                    sx={{ paddingX: 2, textTransform: 'uppercase', cursor: 'pointer' }}
                    variant='caption'
                    width='fit-content'
                    background={row.siteVisitComplete ? '#E6F3E6' : '#FAFDFF'}
                    color={row.siteVisitComplete ? '#01AA59' : '#0071A3'}
                    onClick={(event) => {
                      if(isReadOnly) return;
                      event.stopPropagation()
                      dispatchContext({ type: 'SET_SELECTED_SITE_VISIT_ID', payload: row.siteVisitId })
                      dispatchContext({ type: 'SET_MODAL_OPEN', payload: true })
                      dispatchContext({ type: 'SET_MODAL_TYPE', payload: 'SITE_VISIT' })
                    }}
                  >
                    <Box display='flex' flexDirection='row' gap={1} alignItems='center'>
                      <Icon name='CalendarToday' />
                      <Typography>
                        {`SITE VISIT ${row.siteVisitComplete ? 'COMPLETED' : 'SCHEDULED'}`}
                      </Typography>
                    </Box>
                  </Tag>
                )}
                {siteVisitStatus && (
                  <Tag onClick={(event) => {
                    if(isReadOnly) return;
                    event.stopPropagation()
                    dispatchContext({
                      type: 'SET_SELECTED_SITE_VISIT_ID',
                      payload: row.siteVisitId,
                    })
                    dispatchContext({ type: 'SET_MODAL_OPEN', payload: true })
                    dispatchContext({
                      type: 'SET_MODAL_TYPE',
                      payload: 'SITE_VISIT',
                    })
                  }} variant="caption" width="fit-content" background="#FFFBF0" color={getOrderStatusColor(siteVisitStatus)} center>
                    {' '}
                    <Typography style={{ fontFamily: "LatoBold" }}>
                      {siteVisitStatus?.replace('_', ' ')}
                    </Typography>
                  </Tag>
                )}
                {row.status === ITEM_STATUS.REJECTED && (
                  <Typography color='var(--red600)' sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {`(Client Rejected ${dateFormatString(formatTimestampDate(row.rejectedOn), 'MM-DD-YYYY hh:mm:ss')})`}
                  </Typography>
                )}
              </Box>

              {row.category === 'EVALUATE' && (
                <Grid>
                  {/* {((row.minPriceRange || row.maxPriceRange) && (
                    <Typography>
                      <Typography variant='body1Bold' color='var(--green900)'>
                        Price Range: {getEvaluatePriceRangeString(row.minPriceRange, row.maxPriceRange)}
                      </Typography>
                      { }
                    </Typography>
                  )) || null} */}
                  {row.evaluateReason && <Typography>
                    <Typography variant='body1Bold'>Evaluate Reason: </Typography>{row.evaluateReason}
                  </Typography>}
                </Grid>
              )}

              <Typography variant='body1Bold'>{row.disclaimer}</Typography>
              {row.siteVisitNotes && <Typography><Typography variant='body1Bold'>Site Visit Notes: </Typography>{row.siteVisitNotes}</Typography>}
              {row.punchlistTechNote && <Typography><Typography variant='body1Bold'>Bosscat Tech Notes: </Typography>{row.punchlistTechNote}</Typography>}
              {row.inspectionReportNote && <Typography><Typography variant='body1Bold'>Inspection Notes: </Typography>{row.inspectionReportNote}</Typography>}
              {row.requestedRepairNote && <Typography><Typography variant='body1Bold'>Requested Repair: </Typography>{row.requestedRepairNote}</Typography>}
              {isRejectedItem && followupNotesOrderedByDate?.[0]?.note && <Typography><Typography variant='body1Bold' color='error'>Rejected Notes: </Typography>{`${followupNotesOrderedByDate?.[0]?.note} - ${formatTimestamp(row.followupNotes?.[0]?.createdOn, "MM/DD/YYYY HH:mm")}`}</Typography>}
              {row.fieldNote && <Typography><Typography variant='body1Bold' color='var(--orange500)'>Field Tech Notes: </Typography>{row.fieldNote}</Typography>}
            </Box>
          </Box>
        )
      }
    },
    {
      id: 'quantity', label: 'Qty', align: 'center' as AlignType
    },
    {
      id: 'totalCost',
      label: 'Vendor Price',
      custom: true,
      align: 'center' as AlignType,
      minWidth: 100,
      Element: (row: any): JSX.Element => {
        return (
          <Grid container item direction='column' alignItems='center' textAlign='end'>
            <Typography align='right' variant='body1Bold'>${round(row.totalCost, 2)}</Typography>
            <Typography align='right' fontSize='8px'>${round(row.unitCost, 2)}</Typography>
          </Grid>
        )
      }
    },
    {
      id: 'customerPrice',
      label: 'Customer Price',
      custom: true,
      align: 'center' as AlignType,
      minWidth: 100,
      Element: (row: any): JSX.Element => {
        return (
          <Grid container item direction='column' alignItems='center' textAlign='end'>
            <Typography align='right' variant='body1Bold'>${round(row.totalPrice, 2)}</Typography>
            <Typography align='right' fontSize='8px'>${round(row.unitPrice, 2)}</Typography>
          </Grid>
        )
      }
    },
    {
      id: 'staffReviewed',
      label: 'Reviewed',
      custom: true,
      align: 'center' as AlignType,
      Element: (row: any, index: number): JSX.Element => {
        const vendorIsPaid = orders.find(order => order.orderId === row.orderId)?.vendorPaymentTimestamp !== null || (showAccepted && showComplete)
        return <>
          <Icon name={row.reviewed ? 'CheckBox' : 'CheckBoxOutlineBlank'} color='var(--blue700)' />
          <Box className={styles.Actions}>
            <Button
              disabled={isReadOnly}
              variant='outlined'
              startIcon={<Icon name='Image' />}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation()
                dispatchContext({ type: 'SET_SELECTED_ITEM_ID', payload: row.itemId })
                dispatchContext({ type: 'SET_MODAL_OPEN', payload: true })
                dispatchContext({ type: 'SET_MODAL_TYPE', payload: 'IMAGES' })
              }}
            >
              View Images
            </Button>
            {!vendorIsPaid && <ItemActions itemId={row.itemId} orderId={row.orderId} status={row.status} indexCategory={index} itemCategory={row.category} />}
          </Box>
        </>
      }
    }
  ]

  useEffect(() => {
    fetchItems()
    dispatchContext({ type: 'SET_SELECTED_ORDER_ID', payload: orderId })
  }, [order, id])

  useEffect(() => {
    if (refreshSite) {
      dispatchContext({ type: 'REFRESH_SITE', payload: false })
      fetchItems()
    }
  }, [refreshSite])

  useEffect(() => {
    fetchRepairItems()
  }, [])

  const handleCheckAllWorkItems = (index?: number, checked?: boolean) => {
    const copyWorkItems: GroupedItem[] = [...itemsByCategory]
    const categoryIndex = copyWorkItems.findIndex((item: any) => item.category === copyWorkItems[index as number].category)
    copyWorkItems[categoryIndex].items.forEach((subitem: any) => {
      subitem.checked = !checked
    })
    dispatch(itemsActions.setItemsByCategory(copyWorkItems))
  }

  const fetchItems = () => {

    setTableLoading(true)
    dispatch(itemsActions.fetchItemsByCategory({
      params: { jobId: id, orderId: order }
    }, (_succ) => {
      setTableLoading(false)
    }))
  }

  const fetchRepairItems = () => {
    dispatch(
      territoryActions.fetchTerritoryRepairItems({ territoryId: job?.properties.territory.id, params: { limit: 9999 } })
    )
  }

  const handleClickRow = (row: any) => {
    dispatchContext({ type: 'SET_MODAL_OPEN', payload: true })
    dispatchContext({ type: 'SET_MODAL_TYPE', payload: 'EDIT_ITEM' })
    dispatchContext({ type: 'SET_SELECTED_ITEM_ID', payload: row.itemId })
  }

  /* const handleClickOnHold = () => {
    dispatchContext({ type: 'SET_MODAL_OPEN', payload: true })
    dispatchContext({ type: 'SET_MODAL_TYPE', payload: 'ON_HOLD' })
  } */

  return (
    <Box height='100%'>
      <Header hasItems={itemsByCategory.length > 0} fullHeader={fullHeader} tabName={tabName} />
      <Status />
      <PrivateLoader loading={tableLoading || itemsLoading} sx={{ alignContent: 'flex-start' }}>
        <Box height='100%'>
          {itemsByCategory.length
            ? <>
              {itemsByCategory.map((workItem: GroupedItem, index: number) => {
                return (
                  <DropDownSelect
                    workItem={workItem}
                    key={index}
                    index={index}
                    initiallyExpanded={expandAll}
                    initiallyChecked={checkAll}
                    checkAll={handleCheckAllWorkItems}
                    disabled={isReadOnly}
                  >
                    <Table
                      hasPagination={false}
                      headers={headers}
                      className={styles.Table}
                      small
                      hasBorders={false}
                      rowColor={(row: any) => row.status === ITEM_STATUS.NEW ? '#F1F0FF' : ''}
                      rowAction={isReadOnly ? undefined : handleClickRow}
                      loading={tableLoading}
                    >
                      {workItem.items}
                    </Table>
                  </DropDownSelect>
                )
              })}
              <Box sx={{ padding: 1, background: 'var(--blue800)' }}>
                <Typography color='white'>Excluded Items</Typography>
              </Box>
              <Box sx={{ padding: 2 }}>
                <TextField multiline rows={5} fullWidth placeholder='Write your excluded items' />
              </Box>
            </>
            : <Grid container direction='column' justifyContent='center' alignItems='center' padding={12} height='100%'>
              <img src={computer} alt='computer' />
              <Typography marginTop={2} variant='h6Bold' align='center'>No items added to <br /> this order yet</Typography>
            </Grid>}
        </Box>
      </PrivateLoader>
    </Box>
  )
}

export default OrderItems
